import { call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchAvailableMCLites,
  FetchAvailableSubcontractorsForMCLite,
  AssignSubcontractorToMCLite,
  AssignMCLiteToSubcontractor,
  UnassignSubcontractorToMCLite,
  UnassignMCLiteToSubcontractors,
  FetchMCLiteRelations,
} from 'src/constants/actionTypes'
import { ApiService, callSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { Contractor } from 'src/interfaces/contractor'
import { Pagination } from 'src/interfaces/table'
import { MCLiteRelation } from 'src/interfaces/mcLite'
import { SUBCONTRACTOR_HAS_UNPROCESSED_PAYMENTS } from 'src/constants/mcLite'

interface FetchMCLiteRelationsPayload {
  contractorId: string
}

function* onFetchMCLiteRelations(action: AsyncAction<FetchMCLiteRelationsPayload, MCLiteRelation>) {
  const { contractorId } = action.payload

  try {
    const { payload }: { payload: MCLiteRelation } = yield call(
      callSecureApi,
      Endpoints.FetchMCLiteRelations,
      {
        apiService: ApiService.Paraworks,
        params: [{ field: ':contractorId', value: contractorId }],
      },
    )

    yield put(FetchMCLiteRelations.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch MC-Lite relations'

    yield put(FetchMCLiteRelations.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchAvailableMCLitesPayload {
  search: string
  mcLiteId: string
}

function* onFetchAvailableMCLites(
  action: AsyncAction<
    FetchAvailableMCLitesPayload,
    Array<{ data: Array<Contractor>; pagination: Pagination }>
  >,
) {
  try {
    const { search, mcLiteId } = action.payload

    const { payload }: { payload: Array<{ data: Array<Contractor>; pagination: Pagination }> } =
      yield call(callSecureApi, Endpoints.FetchAvailableMCLites, {
        apiService: ApiService.Paraworks,
        query: {
          search,
        },
        params: [{ field: ':mcLiteId', value: mcLiteId }],
      })

    yield put(FetchAvailableMCLites.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch available MC-Lites'

    yield put(FetchAvailableMCLites.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchAvailableSubcontractorsForMCLitePayload {
  search: string
  mcLiteId: string
}

function* onFetchAvailableSubcontractorsForMCLite(
  action: AsyncAction<
    FetchAvailableSubcontractorsForMCLitePayload,
    Array<{ data: Array<Contractor>; pagination: Pagination }>
  >,
) {
  try {
    const { search, mcLiteId } = action.payload

    const { payload }: { payload: Array<{ data: Array<Contractor>; pagination: Pagination }> } =
      yield call(callSecureApi, Endpoints.FetchAvailableSubcontractorsForMCLite, {
        apiService: ApiService.Paraworks,
        query: {
          search,
        },
        params: [{ field: ':mcLiteId', value: mcLiteId }],
      })

    yield put(FetchAvailableSubcontractorsForMCLite.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage =
      err?.payload?.message || 'Failed to fetch available subcontractors for MC-Lite'

    yield put(FetchAvailableSubcontractorsForMCLite.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface AssignSubcontractorToMCLitePayload {
  subcontractorId: string
  mcLiteId: string
}

function* onAssignSubcontractorToMCLite(
  action: AsyncAction<AssignSubcontractorToMCLitePayload, Array<any>>,
) {
  try {
    const { mcLiteId, subcontractorId } = action.payload

    const { payload }: { payload: Array<any> } = yield call(
      callSecureApi,
      Endpoints.AssignSubcontractorToMCLite,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: {
          subcontractorId,
        },
        params: [{ field: ':mcLiteId', value: mcLiteId }],
      },
    )

    yield put(AssignSubcontractorToMCLite.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to assign subcontractor to MC-Lite'

    yield put(AssignSubcontractorToMCLite.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface AssignMCLiteToSubcontractorPayload {
  mcLiteId: string
  subcontractorId: string
}

function* onAssignMCLiteToSubcontractor(
  action: AsyncAction<AssignMCLiteToSubcontractorPayload, Array<any>>,
) {
  try {
    const { subcontractorId, mcLiteId } = action.payload

    const { payload }: { payload: Array<any> } = yield call(
      callSecureApi,
      Endpoints.AssignMCLiteToSubcontractor,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: {
          mcLiteId,
        },
        params: [{ field: ':subcontractorId', value: subcontractorId }],
      },
    )

    yield put(AssignMCLiteToSubcontractor.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to assign MC-Lite to subcontractor'

    yield put(AssignMCLiteToSubcontractor.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UnassignSubcontractorToMCLitePayload {
  subcontractorId: string
  mcLiteId: string
  templateId?: string
}

function* onUnassignSubcontractorToMCLite(
  action: AsyncAction<UnassignSubcontractorToMCLitePayload, Array<any>>,
) {
  try {
    const { mcLiteId, subcontractorId, templateId } = action.payload

    const { payload }: { payload: Array<any> } = yield call(
      callSecureApi,
      Endpoints.UnassignSubcontractorToMCLite,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: {
          subcontractorId,
          templateId,
        },
        params: [{ field: ':mcLiteId', value: mcLiteId }],
      },
    )

    yield put(UnassignSubcontractorToMCLite.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to unassign subcontractor to MC-Lite'

    yield put(UnassignSubcontractorToMCLite.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UnassignMCLiteToSubcontractorsPayload {
  mcLiteId: string
  subcontractorIds: string[]
}

function* onUnassignMCLiteToSubcontractors(
  action: AsyncAction<UnassignMCLiteToSubcontractorsPayload, Array<any>>,
) {
  try {
    const { subcontractorIds, mcLiteId } = action.payload

    const { payload }: { payload: Array<any> } = yield call(
      callSecureApi,
      Endpoints.UnassignMCLiteToSubcontractors,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        body: {
          subcontractorIds,
        },
        params: [{ field: ':mcLiteId', value: mcLiteId }],
      },
    )

    yield put(UnassignMCLiteToSubcontractors.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const statusCode = err?.payload?.statusCode
    const errorMessage =
      statusCode === 409
        ? SUBCONTRACTOR_HAS_UNPROCESSED_PAYMENTS
        : err?.payload?.message || 'Failed to unassign MC-Lite to subcontractors'

    yield put(UnassignMCLiteToSubcontractors.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* mcLiteSaga() {
  yield takeEvery(FetchMCLiteRelations.type.REQUEST, onFetchMCLiteRelations)
  yield takeEvery(FetchAvailableMCLites.type.REQUEST, onFetchAvailableMCLites)
  yield takeEvery(
    FetchAvailableSubcontractorsForMCLite.type.REQUEST,
    onFetchAvailableSubcontractorsForMCLite,
  )
  yield takeEvery(AssignSubcontractorToMCLite.type.REQUEST, onAssignSubcontractorToMCLite)
  yield takeEvery(AssignMCLiteToSubcontractor.type.REQUEST, onAssignMCLiteToSubcontractor)
  yield takeEvery(UnassignSubcontractorToMCLite.type.REQUEST, onUnassignSubcontractorToMCLite)
  yield takeEvery(UnassignMCLiteToSubcontractors.type.REQUEST, onUnassignMCLiteToSubcontractors)
}
